import React, { forwardRef, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { getPresetButtonDOM } from '../../Helpers/menuItems';
import { print } from 'helpers/print';
import selectors from 'selectors';
import { PRESET_BUTTON_TYPES } from 'constants/customizationVariables';
import useFocusHandler from 'hooks/useFocusHandler';
import FlyoutItemContainer from '../../FlyoutItemContainer';
import useOnDocumentUnloaded from 'src/hooks/useOnDocumentUnloaded';
import useCore from 'hooks/useCore';

/**
 * A button that prints the document.
 * @name printButton
 * @memberof UI.Components.PresetButton
 */
const PrintButton = forwardRef((props, ref) => {
  const { isFlyoutItem, dataElement, className, style, img: icon, title } = props;
  const { core } = useCore();
  const dispatch = useDispatch();

  const useClientSidePrint = useSelector(selectors.useClientSidePrint);
  const isEmbedPrintSupported = useSelector(selectors.isEmbedPrintSupported);
  const sortStrategy = useSelector(selectors.getSortStrategy);
  const colorMap = useSelector(selectors.getColorMap);
  const timezone = useSelector(selectors.getTimezone);
  const activeDocumentViewerKey = useSelector(selectors.getActiveDocumentViewerKey);

  const [document, setDocument] = useState(null);

  useEffect(() => {
    const onDocumentLoaded = (viewerKey) => {
      const document = core.getDocument(viewerKey);
      setDocument(document);
    };

    onDocumentLoaded(activeDocumentViewerKey);
    core.addEventListener('documentLoaded', onDocumentLoaded);
    return () => core.removeEventListener('documentLoaded', onDocumentLoaded);
  }, [activeDocumentViewerKey]);

  const handleDocumentUnloaded = useCallback(() => {
    setDocument(null);
  }, []);
  useOnDocumentUnloaded(handleDocumentUnloaded);

  const handlePrint = () => {
    print(dispatch, useClientSidePrint, isEmbedPrintSupported, sortStrategy, colorMap, { isGrayscale: core.getDocumentViewer().isGrayscaleModeEnabled(), timezone, documentViewerKey: activeDocumentViewerKey });
  };

  const handlePrintButtonClick = useFocusHandler(handlePrint);

  if (!document) {
    if (isFlyoutItem) {
      return null;
    }
    console.warn('The print preset button is not available because no document is loaded.');
  }

  return (
    isFlyoutItem ?
      <FlyoutItemContainer {...props} ref={ref} onClick={handlePrintButtonClick} />
      :
      getPresetButtonDOM({
        buttonType: PRESET_BUTTON_TYPES.PRINT,
        onClick: handlePrintButtonClick,
        isDisabled: !document,
        dataElement,
        className,
        style,
        icon,
        title,
      })
  );
});

PrintButton.propTypes = {
  isFlyoutItem: PropTypes.bool,
  dataElement: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  img: PropTypes.string,
  title: PropTypes.string,
};
PrintButton.displayName = 'PrintButton';

export default PrintButton;